<template>
    <v-card :class="componentName" row>
        <v-toolbar dark tabs color="secondary" height="12">
            <template v-slot:extension>
                <v-tabs
                    v-model="tab"
                    background-color="secondary"
                    ripple
                    slider-color="accent"
                    slider-size="3"
                    show-arrows
                    class="px-4"
                >
                    <v-tab
                        v-for="item in tabKeyNames"
                        :key="item.text"
                        :href="'#tab-' + item.id"
                        :disabled="isAllProcessing"
                    >
                        <v-icon small class="pr-2">
                            {{ item.icon }}
                        </v-icon>
                        {{ $t(item.text) }}
                    </v-tab>
                </v-tabs>
            </template>
        </v-toolbar>
        <v-progress-linear v-show="processing" ref="progress-bar" color="accent" indeterminate class="mt-0 mb-0" />
        <v-card-text>
            <v-tabs-items v-model="tab">
                <v-tab-item v-for="item in tabKeyNames" :key="item.id" :value="'tab-' + item.id">
                    <template v-if="item.id === 1">
                        <login-form
                            :processing="processing"
                            :domain="domainName"
                            :multi-auth="isMultiAuth"
                            :mail-dialog-menu="mailDialogMenu"
                            :saml-enabled="samlEnabled"
                            @login="submitLogin"
                            @open-mail-dialog="showSendMailDialog"
                            @ldap-login="submitLdapLogin"
                            @sso="submitSso"
                        />
                    </template>
                    <template v-else-if="item.id === 2">
                        <hard-device-register-form
                            :processing="processing"
                            :domain="domainName"
                            @hardDeviceRegister="submitHardDeviceRegister"
                        />
                    </template>
                    <template v-else-if="item.id === 3">
                        <login-form
                            :processing="processing"
                            :domain="domainName"
                            action-name-key="Registration"
                            @login="showRegisterDialog"
                            @ldap-login="showRegisterDialog"
                        />
                    </template>
                </v-tab-item>
            </v-tabs-items>
        </v-card-text>
        <the-soft-device-register-dialog
            :is-show="isShowRegisterDialog"
            :user="softDeviceUser"
            @cancel-dialog="isShowRegisterDialog = false"
        />
        <the-send-mail-dialog
            :is-show="isShowMailDialog"
            :menu-title="menuTitle"
            @cancel-dialog="isShowMailDialog = false"
            @send-mail="sendMail"
        />
    </v-card>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { toRefs } from '@vueuse/core';
import { useAuthStore } from '../../../store/modules/auth/composables';
import { LoginForm, HardDeviceRegisterForm } from '../../molecules';
import TheSoftDeviceRegisterDialog from './TheSoftDeviceRegisterDialog.vue';
import TheSendMailDialog from './TheSendMailDialog.vue';
import { useComponentName } from '../../../composables/ComponentName';
import { useRoute } from 'vue-router/composables';
import ScreenUrl from '../../../router/url';

interface TabKeyName {
    id: number;
    text: string;
    icon: string;
}

interface MailMenuEntry {
    id: number;
    title: string;
    name: string;
}

/*
 * properties
 */
interface Props {
    domainName?: string;
    samlEnabled?: boolean;
    isMultiAuth?: boolean;
    tabKeyNames?: Array<TabKeyName>;
    mailMenu?: Array<MailMenuEntry>;
}
const props = withDefaults(defineProps<Props>(), {
    domainName: '',
    samlEnabled: false,
    isMultiAuth: false,
    tabKeyNames: () => [],
    mailMenu: () => [],
});
const { domainName, samlEnabled, isMultiAuth, tabKeyNames, mailMenu } = toRefs(props);

/*
 * composables
 */
const {
    login,
    ldapLogin,
    processing,
    softRegistered,
    sso,
    multifactorLogin,
    ldapMultifactorLogin,
    hardDeviceRegister,
    sendUnlockMail,
    sendPasswordResetMail,
    sendUnconfirmedMail,
} = useAuthStore();
const { componentName } = useComponentName();
const route = useRoute();

/*
 * getter,setters
 */

const tab = ref('tab-1');
const isShowRegisterDialog = ref(false);
const softDeviceUser = ref({
    uid: '',
    password: '',
});
const menu = ref(0);
const menuTitle = ref('');
const isShowMailDialog = ref(false);

const mailDialogMenu = computed(() => mailMenu.value);
const isAllProcessing = computed(() => processing.value || softRegistered.value.isProcessing);

/**
 * methods
 */
function submitLogin(authData: { uid: string; password: string; onetimePassword: string }): void {
    if (isMultiAuth.value) {
        multifactorLogin({ ...authData, redirect: queryRedirect() });
    } else {
        login({ ...authData, redirect: queryRedirect() });
    }
}
function submitLdapLogin(authData: { uid: string; password: string; onetimePassword: string }): void {
    if (isMultiAuth.value) {
        ldapMultifactorLogin({ ...authData, redirect: queryRedirect() });
    } else {
        ldapLogin({ ...authData, redirect: queryRedirect() });
    }
}
function submitSso(): void {
    sso();
}
function submitHardDeviceRegister(registData: { uid: string; password: string; serial_number: string }): void {
    hardDeviceRegister(registData);
}
function showRegisterDialog(authData: { uid: string; password: string }): void {
    softDeviceUser.value = authData;
    isShowRegisterDialog.value = true;
}
function showSendMailDialog(menuNumber: number): void {
    menu.value = menuNumber;
    menuTitle.value = mailDialogMenu.value.find(menu => menu.id == menuNumber).title;
    isShowMailDialog.value = true;
}
async function sendMail(email: string) {
    if (menu.value === 1) {
        await sendUnlockMail(email);
    } else if (menu.value === 2) {
        await sendPasswordResetMail(email);
    } else {
        await sendUnconfirmedMail(email);
    }
    isShowMailDialog.value = false;
}

/**
 * ログイン成功時の移動先
 *
 * クエリパラメータで指定されている場合はそのURL
 * 指定されていない場合は トップ画面(ダッシュボード)のURL
 */
function queryRedirect() {
    if (!route.query.redirect) {
        return ScreenUrl.TOP;
    }
    if (typeof route.query.redirect !== 'string') {
        return ScreenUrl.TOP;
    }
    return route.query.redirect;
}
</script>
